import CheckIcon from '@mui/icons-material/Check';
import HighlightOffIcon from '@mui/icons-material/HighlightOff';
import SettingsApplicationsIcon from '@mui/icons-material/SettingsApplications';
import TryOutlinedIcon from '@mui/icons-material/TryOutlined';
import { Box, Button, Card, Slide, Typography } from '@mui/material';
import { useEffect, useMemo, useState } from 'react';
import useChangeOrderShipper from '../../../../../hooks/orders/useChangeOrderShipper';
import useGetSavOrdersByOrderId from '../../../../../hooks/orders/useGetSavOrdersByOrderId';
import useSendInsdes from '../../../../../hooks/orders/useSendInsdes';
import useSendToSto from '../../../../../hooks/orders/useSendToSto';
import useOrderDetails from '../../../../../hooks/project/useOrderDetails';
import DesiredShippingDateIcon from '../../../../svg-icons/DesiredShippingDateIcon';
import OrderCancelIcon from '../../../../svg-icons/OrderCancelIcon';
import OrderValidatedIcon from '../../../../svg-icons/OrderValidatedIcon';
import OrderWaitingSCIcon from '../../../../svg-icons/OrderWaitingSCIcon';
import RefundAskIcon from '../../../../svg-icons/RefundAskIcon';
import RetractionFeesIcon from '../../../../svg-icons/RetractionFeesIcon';
import RetractionIcon from '../../../../svg-icons/RetractionIcon';
import SAVOrderIcon from '../../../../svg-icons/SAVOrderIcon';
import ShippingAddressIcon from '../../../../svg-icons/ShippingAddressIcon';
import QuoteIcon from '../../../../utils/common/QuoteIcon.jsx';
import CancelOrderForm from './Cancels/CancelOrderForm.jsx';
import EmptyDraftConfirm from './Cancels/EmptyDraftConfirm.jsx';
import OrderCancelled from './Cancels/OrderCancelledDetails.jsx';
import QuoteForm from './Quotes/Form.jsx';
import Refund from './Refunds/Refund.jsx';
import RenderActionButtons from './RenderActionButtons.jsx';
import ManageRetraction from './Retractions/ManageRetraction.jsx';
import RetractionFeesForm from './Retractions/RetractionFeesForm.jsx';
import RetractionFormSAV from './Retractions/RetractionFormSAV.jsx';
import RetractionFormSLO from './Retractions/RetractionFormSLO.jsx';
import RetractLegalDelayOverDue from './Retractions/RetractLegalDelayOverDue.jsx';
import ManageSav from './Sav/ManageSav';
import SavOrderDelayOverDue from './Sav/SavDelayOverDue';
import SavOrderForm from './Sav/SavOrderForm.jsx';
import DesiredShippingDateForm from './Update/DesiredShippingDateForm.jsx';
import ShippingAddressForm from './Update/ShippingAddressForm.jsx';
import UpdateOrderStatus from './Update/UpdateOrderStatus.jsx';
import {
  isButtonDisplayed,
  isDelayOverDue,
  isSCButtonDisplayed,
  isWaitSCAuthorized,
} from './utils';

// Règle de blocage iso backend (`CreateSAVOrdersSerializer.validate`) :
// une nouvelle SAV est interdite uniquement s'il existe déjà une SAV "ouverte"
// (statut RECORDED/PENDING) préparée par Yusen ou Noyon. Le préparateur Bureau
// (STO010002) est volontairement exclu de la liste bloquante.
const OPEN_SAV_STATUS_CODES = ['RECORDED', 'PENDING'];
const BLOCKING_STO_REFS = ['STO010001', 'STO010003']; // Noyon, Yusen
const CHANGE_SHIPPER_ALLOWED_STATUS = [
  'WAITING_SC',
  'VALIDATED',
  'SEND_TO_STO',
  'PREPARATION_CONFIRMED',
];
const SHIPPING_ADDRESS_ALLOWED_STATUS = [
  'WAITING_SC',
  'VALIDATED',
  'SEND_TO_STO',
  'PREPARATION_CONFIRMED',
];
const SHIPPER_OPTIONS = ['DPD', 'DPD_SICAAN', 'UPS'];
const ACTION_ALLOWED_ROLE_NAMES = ['Responsable SLO', 'Administrateur'];
const ACTION_ROLE_TOOLTIP =
  'Action reservee aux roles Responsable SLO ou Administrateur.';

const OrderActions = ({ ticket, mutateTicket, profile }) => {
  const [component, setComponent] = useState(null);
  const [delayOverDue, setDelayOverDue] = useState(false);
  const order = useMemo(() => ticket?.order, [ticket?.order]);

  const { orderDetails, isLoading } = useOrderDetails(order?._id);
  const { changeOrderShipper } = useChangeOrderShipper();
  const { sendInsdes } = useSendInsdes();
  const { sendToSto } = useSendToSto();

  const hasExistingDraftQuote = useMemo(
    () => ticket?.quotes?.some((quote) => quote.status.code === 'DRAFT'),
    [ticket]
  );
  const draftQuote = useMemo(
    () =>
      hasExistingDraftQuote
        ? ticket?.quotes?.find((quote) => quote.status.code === 'DRAFT')
        : false,
    [ticket, hasExistingDraftQuote]
  );

  // TODO: skip props for testing
  const handleShowComponent = (component, skip) => {
    setDelayOverDue(isDelayOverDue(orderDetails?.dates?.createdAt, 'month', 2));
    if (
      component === 'retractOrderSAV' &&
      orderDetails?.legalDelayOverDue &&
      !skip
    ) {
      setComponent('retractLegalDelayOverDue');
      return;
    }
    if (component === 'savOrder' && delayOverDue && !skip) {
      setComponent('savOrderDelayOverDue');
      return;
    }

    if (component === 'cancelOrder') {
      const isExistingDraft = Boolean(ticket?.draft);

      if (isExistingDraft && !skip) {
        setComponent('cancelOrderDraft');
        return;
      }
    }

    if (component === null) {
      mutateTicket(undefined, { revalidate: true });
    }
    setComponent(component);
  };

  const handleCloseSnackBar = () => {
    mutateTicket();
  };

  const handleSendInsdesAction = async () => {
    const orderReference = orderDetails?.reference || order?.reference;
    if (!orderReference) {
      return;
    }
    const isConfirmed = window.confirm(
      `Envoyer le flux EDI INSDES pour la commande ${orderReference} ?`
    );
    if (!isConfirmed) {
      return;
    }
    await sendInsdes(orderReference);
  };

  const handleSendToStoAction = async () => {
    const orderReference = orderDetails?.reference || order?.reference;
    if (!orderReference) {
      return;
    }
    const isConfirmed = window.confirm(
      `Envoyer la commande ${orderReference} en preparation ?`
    );
    if (!isConfirmed) {
      return;
    }
    await sendToSto(orderReference);
  };

  const handleChangeShipperAction = async () => {
    const orderReference = orderDetails?.reference || order?.reference;
    if (!orderReference) {
      return;
    }
    const selectedShipper = window.prompt(
      `Choisir le transporteur (${SHIPPER_OPTIONS.join(', ')})`,
      orderDetails?.shipper || ''
    );
    if (!selectedShipper) {
      return;
    }

    const normalizedShipper = selectedShipper.trim().toUpperCase();
    const isConfirmed = window.confirm(
      `Changer le transporteur de la commande ${orderReference} vers ${normalizedShipper} ?`
    );
    if (!isConfirmed) {
      return;
    }

    await changeOrderShipper(orderReference, normalizedShipper);
  };

  const hasActiveSAVDraft =
    !!orderDetails?.activeSavDraft?.isSaved ||
    orderDetails?.activeSavDraft?.lines?.length > 0;
  const isSendToStoAllowed = orderDetails?.status?.code === 'VALIDATED';
  const isChangeShipperAllowed = CHANGE_SHIPPER_ALLOWED_STATUS.includes(
    orderDetails?.status?.code
  );
  const isInsdesStatusAllowed = [
    'PREPARATION_CONFIRMED',
    'SEND_TO_STO',
  ].includes(orderDetails?.status?.code);
  const isYusenOrder = orderDetails?.orderPicker === 'STO010003';
  const hasSloManagerOrAdministratorRole = (profile?.roles || []).some((role) =>
    ACTION_ALLOWED_ROLE_NAMES.includes(role?.name)
  );
  const isSendToStoBusinessAllowed = isSendToStoAllowed && !!order?.reference;
  const isChangeShipperBusinessAllowed =
    isChangeShipperAllowed && !!order?.reference;
  const isInsdesBusinessAllowed =
    isInsdesStatusAllowed && isYusenOrder && !!order?.reference;
  const isShippingAddressAllowed = SHIPPING_ADDRESS_ALLOWED_STATUS.includes(
    orderDetails?.status?.code
  );
  const getActionTooltip = (isBusinessAllowed, businessTooltip) => {
    if (!isBusinessAllowed) {
      return businessTooltip;
    }
    if (!hasSloManagerOrAdministratorRole) {
      return ACTION_ROLE_TOOLTIP;
    }
    return '';
  };

  // Au moins une SAV est rattachée à la commande (peu importe son statut).
  const hasSavOrder = orderDetails?.savOrders?.length > 0;

  // SAV détaillées (statut + préparateur) : nécessaires pour répliquer la règle
  // backend. On ne déclenche la requête que si la commande possède déjà des SAV.
  const { savOrders: linkedSavOrders } = useGetSavOrdersByOrderId(
    hasSavOrder ? order?._id : null
  );

  // Une SAV "ouverte" (RECORDED/PENDING) chez Yusen ou Noyon bloque la création
  // d'une nouvelle SAV, comme le fait le backend. Sinon (SAV Bureau, ou SAV dans
  // un statut ultérieur : envoyée STO, livrée, annulée…), la création reste possible.
  const hasOpenBlockingSav = useMemo(
    () =>
      (linkedSavOrders || []).some((sav) => {
        const statusCode =
          sav?.status?.code || sav?.savDatasFromBO?.status?.code;
        const pickerRef = sav?.savDatasFromBO?.order_picker || sav?.orderPicker;
        return (
          OPEN_SAV_STATUS_CODES.includes(statusCode) &&
          BLOCKING_STO_REFS.includes(pickerRef)
        );
      }),
    [linkedSavOrders]
  );

  // Services Dealt encore actifs (non annulés) : le BO refuse l'annulation tant
  // qu'ils ne sont pas annulés côté prestataire. On désactive alors le bouton en
  // amont avec un message explicite.
  const activeDealtServices = orderDetails?.activeDealtServices || [];
  const hasActiveDealtService = activeDealtServices.length > 0;
  const activeDealtServicesLabel = activeDealtServices
    .map((service) => service?.reference)
    .filter(Boolean)
    .join(', ');

  const actions = [
    {
      icon: <RefundAskIcon fontSize="large" />,
      textContent: 'Demander un remboursement',
      onClick: () => handleShowComponent('refund'),
      isDisabled:
        !ticket.order || ticket?.order?.price === ticket?.order?.amountRefunded,
      isDisplayed: true,
    },
    {
      icon: <SAVOrderIcon fontSize="large" />,
      textContent: hasActiveSAVDraft
        ? 'Reprendre brouillon SAV'
        : 'Générer une SAV',
      onClick: () => handleShowComponent('savOrder'),
      // Désactivé (avec tooltip) tant qu'une SAV ouverte Yusen/Noyon existe,
      // conformément à la règle de création du backend.
      isDisabled: hasOpenBlockingSav,
      tooltip: hasOpenBlockingSav
        ? 'Une SAV ouverte (Yusen/Noyon) existe déjà'
        : '',
      isDisplayed:
        (orderDetails?.status?.code === 'BEING_DELIVERED_TO_CUSTOMER' ||
          orderDetails?.status?.code === 'TAV_DELIVERY_SUCCEED' ||
          orderDetails?.status?.code === 'PAYMENT_PROCEED') &&
        orderDetails?.type?.code !== 'ORDER_SAV',
    },
    {
      icon: <SettingsApplicationsIcon fontSize="large" />,
      textContent: 'Gérer les SAVs',
      onClick: () => handleShowComponent('manageSav'),
      isDisabled: false,
      // Gestion découplée de la création : affiché dès qu'au moins une SAV existe.
      isDisplayed: orderDetails?.type?.code !== 'ORDER_SAV' && hasSavOrder,
    },
    {
      icon: <OrderCancelIcon fontSize="large" />,
      textContent: 'Demander une annulation',
      onClick: () => handleShowComponent('cancelOrder'),
      // On ne bloque plus l'annulation MP sur le statut de
      // paiement côté UI. Le backend/BO garde la validation métier sur les
      // statuts de commande annulables.
      // On bloque en revanche tant qu'un service Dealt associé est encore actif
      // (doit être annulé chez le prestataire avant l'annulation de la commande).
      isDisabled: !ticket?.order?.isCancellable || hasActiveDealtService,
      tooltip: hasActiveDealtService
        ? `Service Dealt à annuler avant l'annulation : ${activeDealtServicesLabel}`
        : '',
      isDisplayed: isButtonDisplayed({ order }),
    },
    {
      icon:
        ticket?.order?.status?.code === 'VALIDATED' ? (
          <OrderWaitingSCIcon fontSize="large" />
        ) : (
          <OrderValidatedIcon fontSize="large" />
        ),
      textContent:
        ticket?.order?.status?.code === 'VALIDATED'
          ? 'Mettre en attente SC'
          : 'Valider la commande',
      onClick: () => handleShowComponent('updateOrderStatus'),
      isDisabled: isWaitSCAuthorized(orderDetails),
      isDisplayed: isSCButtonDisplayed({ order }),
    },
    {
      icon: <SettingsApplicationsIcon fontSize="large" />,
      textContent: 'Envoyer en preparation',
      onClick: handleSendToStoAction,
      isDisabled:
        !isSendToStoBusinessAllowed || !hasSloManagerOrAdministratorRole,
      tooltip: getActionTooltip(
        isSendToStoBusinessAllowed,
        "Action disponible uniquement pour les commandes au statut 'Validated'"
      ),
      isDisplayed: true,
    },
    {
      icon: <SettingsApplicationsIcon fontSize="large" />,
      textContent: 'Changer transporteur',
      onClick: handleChangeShipperAction,
      isDisabled:
        !isChangeShipperBusinessAllowed || !hasSloManagerOrAdministratorRole,
      tooltip: getActionTooltip(
        isChangeShipperBusinessAllowed,
        "Action disponible pour 'Waiting SC', 'Validated', 'Envoyé au STO' et 'En cours de traitement STO'"
      ),
      isDisplayed: true,
    },
    {
      icon: <SettingsApplicationsIcon fontSize="large" />,
      textContent: 'Envoyer flux EDI INSDES',
      onClick: handleSendInsdesAction,
      isDisabled: !isInsdesBusinessAllowed || !hasSloManagerOrAdministratorRole,
      tooltip: getActionTooltip(
        isInsdesBusinessAllowed,
        "Action disponible uniquement pour les commandes Yusen en statut 'Envoyé au STO' ou 'En cours de traitement STO'"
      ),
      isDisplayed: true,
    },
    {
      icon: <DesiredShippingDateIcon fontSize="large" />,
      textContent: "Date d'expédition souhaitée",
      onClick: () => handleShowComponent('editDesiredShippingDate'),
      isDisabled: orderDetails?.status?.code !== 'WAITING_SC',
      isDisplayed:
        (ticket?.order?.status?.code === 'VALIDATED' ||
          ticket?.order?.status?.code === 'WAITING_SC') &&
        (!ticket?.isFromMP || !!ticket?.order?.mpId),
    },
    {
      icon: <ShippingAddressIcon fontSize="large" />,
      textContent: 'Adresse de livraison',
      onClick: () => handleShowComponent('editShippingAddress'),
      isDisabled: !isShippingAddressAllowed,
      isDisplayed:
        ticket?.order?.status?.code === 'VALIDATED' ||
        ticket?.order?.status?.code === 'WAITING_SC' ||
        ticket?.order?.status?.code === 'SEND_TO_STO' ||
        ticket?.order?.status?.code === 'PREPARATION_CONFIRMED',
    },
    {
      icon: <RetractionIcon fontSize="large" />,
      textContent: 'Demander une retractation (SAV)',
      onClick: () => handleShowComponent('retractOrderSAV'),
      isDisabled: false,
      isDisplayed:
        (orderDetails?.status?.code === 'BEING_DELIVERED_TO_CUSTOMER' ||
          orderDetails?.status?.code === 'TAV_DELIVERY_SUCCEED') &&
        !orderDetails?.retraction?.isDraft &&
        orderDetails?.type?.code !== 'ORDER_SAV',
    },
    {
      icon: <HighlightOffIcon fontSize="large" color="inherit" />,
      textContent: 'Organiser un enlèvement (Rétractation)',
      onClick: () => handleShowComponent('retractOrderSLO'),
      isDisabled: false,
      isDisplayed:
        (orderDetails?.status?.code === 'BEING_DELIVERED_TO_CUSTOMER' ||
          orderDetails?.status?.code === 'TAV_DELIVERY_SUCCEED') &&
        orderDetails?.retraction !== null &&
        orderDetails?.type?.code !== 'ORDER_SAV',
    },
    {
      icon: <RetractionFeesIcon fontSize="large" />,
      textContent: 'Simuler des frais de retractation',
      onClick: () => handleShowComponent('retractOrderFees'),
      isDisabled: false,
      isDisplayed:
        (orderDetails?.status?.code === 'BEING_DELIVERED_TO_CUSTOMER' ||
          orderDetails?.status?.code === 'TAV_DELIVERY_SUCCEED') &&
        orderDetails?.type?.code !== 'ORDER_SAV',
    },
    {
      icon: <CheckIcon fontSize="large" color="inherit" />,
      textContent: 'Confirmer ma rétractation',
      onClick: () => handleShowComponent('confirmRetraction'),
      isDisabled: false,
      isDisplayed:
        orderDetails?.status?.code === 'RETRACTATION_IN_PROGRESS' &&
        orderDetails?.type?.code !== 'ORDER_SAV',
    },
    {
      icon: <SettingsApplicationsIcon fontSize="large" color="inherit" />,
      textContent: 'Gérer ma rétractation',
      onClick: () => handleShowComponent('manageRetraction'),
      isDisabled: false,
      isDisplayed:
        orderDetails?.status?.code === 'RETRACTATION_IN_PROGRESS' &&
        orderDetails?.type?.code !== 'ORDER_SAV',
    },
    {
      icon: <QuoteIcon boxSize={34} disabled={false} />,
      textContent: hasExistingDraftQuote
        ? 'Reprendre le devis'
        : 'Créer un devis',
      onClick: () => handleShowComponent('createQuote'),
      isDisabled: false,
      isDisplayed: true,
    },
    {
      icon: <TryOutlinedIcon boxSize={34} disabled={false} />,
      textContent: 'Remontée qualité',
      onClick: () => {
        // Copie le numéro de commande dans le presse-papier
        if (order?.reference) {
          navigator.clipboard
            .writeText(order.reference)
            .then(() => {
              console.log('Numéro de commande copié:', order.reference);
            })
            .catch((err) => {
              console.error('Erreur lors de la copie:', err);
            });
        }
        // Ouvre le formulaire Google dans un nouvel onglet
        window.open(
          'https://docs.google.com/forms/d/e/1FAIpQLScsBNDY2VCSvpe1KEpU-IctMRAG3qaVV4Qb-6chBloijWTOjA/viewform?pli=1',
          '_blank',
          'noopener,noreferrer'
        );
      },
      isDisabled: false,
      isDisplayed: true,
    },
  ];

  useEffect(() => {
    // affiche le recap de l'annulation,
    if (order?.status?.code === 'CANCELED_BY_SC') {
      // ne met pas à jour le component si le remboursement est en cours
      if (
        component === 'refund' &&
        ticket?.order?.amountRefunded < ticket?.order?.price
      ) {
        return;
      }
      setComponent('orderCancelled');
    }
  }, [order, component]);

  // console.log('orderDetails', orderDetails?.paymentStatus?.code);
  // console.log('orderDetails', ticket?.isFromMP);
  // console.log(
  //   'test',
  //   ticket?.isFromMP &&
  //     ticket?.order?.paymentStatus?.code === 'PAYMENT_CAPTURED'
  // );

  return (
    <>
      {ticket?.isQualified === false ? (
        <Box sx={{ marginTop: 2, display: 'flex', justifyContent: 'center' }}>
          <Card
            elevation={5}
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: 2,
              width: '100%',
              padding: 2,
            }}
          >
            <Box
              sx={{
                display: 'flex',
                gap: 2,
                alignItems: 'center',
              }}
            >
              <Typography color="primary">
                <span style={{ fontWeight: 'bold' }}>Action sur commande</span>{' '}
                La qualification du ticket doit d&apos;abord être validée.
                Onglet &quot;Répondre&quot;
              </Typography>
            </Box>
          </Card>
        </Box>
      ) : (
        <>
          {!component && (
            <RenderActionButtons
              actions={actions}
              isLoading={isLoading}
              ticket={ticket}
            />
          )}
          <Slide direction="up" in>
            <Box>
              {component === 'orderCancelled' && (
                <>
                  {[
                    'payment_captured',
                    'partial_refund_pending',
                    'partial_refund_failed',
                    'payment_partially_refunded',
                  ].includes(ticket?.order?.paymentStatus) &&
                    ticket?.order?.amountRefunded < ticket?.order?.price && (
                      <Box sx={{ width: 'calc(33.33% - 16px)' }}>
                        <Button
                          sx={{
                            width: '100%',
                            height: 100,
                            display: 'flex',
                            flexDirection: 'row',
                            justifyContent: 'center',
                            padding: 4,
                            cursor: 'pointer',
                            gap: 1,
                            borderRadius: 2,
                            color: 'primary.main',
                            fontWeight: 'bold',
                            marginBottom: '10px',
                          }}
                          onClick={() => handleShowComponent('refund')}
                          variant="outlined"
                        >
                          <RefundAskIcon fontSize="large" />
                          Demander un remboursement
                        </Button>
                      </Box>
                    )}
                  <OrderCancelled ticket={ticket} />
                </>
              )}
              {component === 'cancelOrderDraft' && (
                <EmptyDraftConfirm
                  ticket={ticket}
                  handleShowComponent={handleShowComponent}
                />
              )}
              {component === 'refund' && (
                <Refund
                  ticket={ticket}
                  mutateTicket={mutateTicket}
                  handleShowComponent={handleShowComponent}
                  order={orderDetails}
                />
              )}
              {component === 'cancelOrder' && (
                <CancelOrderForm
                  ticket={ticket}
                  handleShowComponent={handleShowComponent}
                  profile={profile}
                />
              )}
              {component === 'savOrder' && (
                <SavOrderForm
                  orderDetails={{ ...orderDetails, ticket: ticket }}
                  user={profile}
                  delayOverDue={delayOverDue}
                  activeSavDraftId={orderDetails?.activeSavDraft?._id}
                  mutateTicket={mutateTicket}
                  handleShowComponent={handleShowComponent}
                />
              )}
              {component === 'updateOrderStatus' && (
                <UpdateOrderStatus
                  order={order}
                  profile={profile}
                  handleShowComponent={handleShowComponent}
                  ticketId={ticket?._id}
                />
              )}
              {component === 'editDesiredShippingDate' && (
                <DesiredShippingDateForm
                  ticket={ticket}
                  profile={profile}
                  handleShowComponent={handleShowComponent}
                  handleCloseSnackBar={handleCloseSnackBar}
                />
              )}
              {component === 'editShippingAddress' && (
                <ShippingAddressForm
                  ticket={ticket}
                  profile={profile}
                  shippingAddress={orderDetails?.addresses?.shipping}
                  handleShowComponent={handleShowComponent}
                  handleCloseSnackBar={handleCloseSnackBar}
                />
              )}
              {component === 'retractOrderSAV' && (
                <RetractionFormSAV
                  orderRef={order?.reference}
                  orderDetails={{ ...orderDetails, ticket: ticket }}
                  userId={profile?._id}
                  handleShowComponent={handleShowComponent}
                />
              )}
              {component === 'retractOrderSLO' && (
                <RetractionFormSLO
                  orderRef={order?.reference}
                  orderDetails={{ ...orderDetails, ticket: ticket }}
                  userId={profile?._id}
                  handleShowComponent={handleShowComponent}
                  retraction={orderDetails?.retraction}
                />
              )}
              {component === 'retractOrderFees' && (
                <RetractionFeesForm
                  orderDetails={{ ...orderDetails, ticket: ticket }}
                  handleShowComponent={handleShowComponent}
                  getRetractionFees={() => {}}
                />
              )}
              {component === 'retractLegalDelayOverDue' && (
                <RetractLegalDelayOverDue
                  legalDelayOverDue={orderDetails?.legalDelayOverDue}
                  handleShowComponent={handleShowComponent}
                  store={orderDetails?.contact?.store}
                />
              )}
              {component === 'savOrderDelayOverDue' && (
                <SavOrderDelayOverDue
                  savDelayOverDue={orderDetails?.dates?.createdAt}
                  handleShowComponent={handleShowComponent}
                />
              )}
              {component === 'manageRetraction' && (
                <ManageRetraction
                  orderDetails={{ ...orderDetails, ticket: ticket }}
                  handleShowComponent={handleShowComponent}
                  profile={profile}
                />
              )}
              {component === 'confirmRetraction' && (
                <ManageRetraction
                  orderDetails={{ ...orderDetails, ticket: ticket }}
                  handleShowComponent={handleShowComponent}
                  profile={profile}
                  isConfirm={true}
                />
              )}
              {component === 'manageSav' && (
                <ManageSav
                  orderDetails={{ ...orderDetails, ticket: ticket }}
                  handleShowComponent={handleShowComponent}
                  profile={profile}
                />
              )}
              {component === 'createQuote' && (
                <QuoteForm
                  ticket={ticket}
                  handleShowComponent={handleShowComponent}
                  profile={profile}
                  draftQuote={draftQuote}
                  mutateTicket={mutateTicket}
                />
              )}
            </Box>
          </Slide>
        </>
      )}
    </>
  );
};

export default OrderActions;
